// Invites the current agent has SENT to a visitor and is waiting on a
// response for. Mirror of `incoming-calls.ts` but for the caller side.
//
// Also tracks which sessions we were the caller on — `Conversation.tsx`
// reads this on inVideo=true to decide whether to start an offer (caller)
// or watch for an offer (callee).

import { create } from 'zustand';
import type { SessionId, VisitorId } from '@prakash-software/shared';

export interface OutgoingCall {
  sessionId: SessionId;
  to: VisitorId;
  toName: string;
  at: number;
}

interface OutgoingCallsState {
  pending: Map<SessionId, OutgoingCall>;
  /** Sessions the agent initiated the video on. Survives outgoingCall drop. */
  callerSessions: Set<SessionId>;
  push: (call: OutgoingCall) => void;
  drop: (sessionId: SessionId) => void;
  markCaller: (sessionId: SessionId) => void;
  clearCaller: (sessionId: SessionId) => void;
}

export const useOutgoingCallsStore = create<OutgoingCallsState>((set) => ({
  pending: new Map(),
  callerSessions: new Set(),
  push: (call) =>
    set((s) => {
      const next = new Map(s.pending);
      next.set(call.sessionId, call);
      return { pending: next };
    }),
  drop: (sessionId) =>
    set((s) => {
      if (!s.pending.has(sessionId)) return s;
      const next = new Map(s.pending);
      next.delete(sessionId);
      return { pending: next };
    }),
  markCaller: (sessionId) =>
    set((s) => {
      if (s.callerSessions.has(sessionId)) return s;
      const next = new Set(s.callerSessions);
      next.add(sessionId);
      return { callerSessions: next };
    }),
  clearCaller: (sessionId) =>
    set((s) => {
      if (!s.callerSessions.has(sessionId)) return s;
      const next = new Set(s.callerSessions);
      next.delete(sessionId);
      return { callerSessions: next };
    }),
}));
